// Ported from Octo.Mobile@79a7c69 src/api/systemApi.ts
import { apiClient } from './http/client';
import { ENDPOINTS } from './endpoints';
import type { AuthLoginResponse, RegisterSessionRequest } from '@/shared/types/api';
import type {
  SystemScreen,
  SystemObject,
  SystemObjectField,
  SystemObjectFilter,
  SystemRoleObject,
  SystemRoleObjectField,
  SystemObjectAction,
  SystemRoleObjectAction,
  SystemHtmlCardTemplate,
  SystemHtmlSummaryTemplate,
  SystemRazorTemplate,
  SystemIcon,
  SystemNodeActionType,
  UserScreenNode,
} from '@/shared/types/system';
import type { MetadataStampResponse } from '@/shared/types/api';

// User model matching SystemUser from backend
interface SystemUser {
  UserId: string;
  RoleId: string;
  LegacyUserId: number;
  UserCode: string;
  UserName: string;
  KullaniciKodu: string;
  KullaniciAdi: string;
  Email: string;
  Active: boolean;
  ProfileImage: string | null;
  CanUseAiChat?: boolean;
  CanUseChat?: boolean;
  /** Web lisansı (dolu ise HasWebLicense) */
  LicenseId?: string | null;
  /** Node lisansı (dolu ise HasNodeLicense) */
  NodeLicenseId?: string | null;
  PrefersNodeView?: boolean;
}

export const systemApi = {
  // Auth - JWT
  authLogin: (userCode: string, password: string, appId: string = 'Octo.Mobile'): Promise<AuthLoginResponse> =>
    apiClient.post<AuthLoginResponse>(ENDPOINTS.AUTH_LOGIN, { userCode, password, appId }),

  authRefresh: (refreshToken: string): Promise<AuthLoginResponse> =>
    apiClient.post<AuthLoginResponse>(ENDPOINTS.AUTH_REFRESH, { refreshToken }),

  authLogout: (): Promise<void> =>
    apiClient.post<void>(ENDPOINTS.AUTH_LOGOUT),

  registerSession: (request: RegisterSessionRequest): Promise<boolean> =>
    apiClient.post<boolean>(ENDPOINTS.REGISTER_SESSION, {
      SessionId: request.sessionId,
      UserId: request.userId,
      AppName: 'Octo.Mobile',
      ClientInfo: `${request.platform} - ${request.deviceInfo}`,
      LoginTime: new Date().toISOString(),
      IsSuccess: true,
    }),

  closeSession: (sessionId: string): Promise<boolean> =>
    apiClient.post<boolean>(ENDPOINTS.CLOSE_SESSION(sessionId)),

  // Users
  getUser: (userId: string): Promise<SystemUser> =>
    apiClient.get<SystemUser>(ENDPOINTS.USER(userId)),

  getAllUsers: (): Promise<SystemUser[]> =>
    apiClient.get<SystemUser[]>(ENDPOINTS.ALL_USERS),

  // Screens
  getScreens: (): Promise<SystemScreen[]> =>
    apiClient.get<SystemScreen[]>(ENDPOINTS.SCREENS),

  getScreen: (screenId: string): Promise<SystemScreen> =>
    apiClient.get<SystemScreen>(ENDPOINTS.SCREEN(screenId)),

  /**
   * Menü ağacı. `UserScreens` yalnızca yetkili satırları döndürüyor; bir modüle yetkisi
   * olmayan kullanıcının altındaki ekran kopuk kalıyor ve kök seviyesine düşüyordu.
   * Bu uç zinciri sunucuda tamamlıyor.
   */
  getMenuScreens: (userId: string): Promise<UserScreenNode[]> =>
    apiClient.get<UserScreenNode[]>(ENDPOINTS.MENU_SCREENS(userId)),

  getUserScreens: (userId: string): Promise<UserScreenNode[]> =>
    apiClient.get<UserScreenNode[]>(ENDPOINTS.USER_SCREENS(userId)),

  // Girişte Node görünümü tercihi — DB'de tutulur, her cihazda geçerli (web: PrefersNodeView)
  getNodeViewPreference: (userId: string): Promise<boolean> =>
    apiClient.get<boolean>(ENDPOINTS.NODE_VIEW_PREFERENCE(userId)),
  updateNodeViewPreference: async (userId: string, prefersNodeView: boolean): Promise<{ success: boolean; message: string | null }> => {
    const res = await apiClient.post<{ success?: boolean; Success?: boolean; message?: string; Message?: string }>(
      ENDPOINTS.UPDATE_NODE_VIEW_PREFERENCE,
      { UserId: userId, PrefersNodeView: prefersNodeView }
    );
    return { success: !!(res?.success ?? res?.Success), message: (res?.message ?? res?.Message) ?? null };
  },

  toggleFavorite: (userId: string, screenId: string): Promise<{ Success: boolean; IsFavorite: boolean }> =>
    apiClient.post<{ Success: boolean; IsFavorite: boolean }>(ENDPOINTS.TOGGLE_FAVORITE, {
      KullaniciID: userId,
      ScreenID: screenId,
    }),

  // Objects
  getObjects: (): Promise<SystemObject[]> =>
    apiClient.get<SystemObject[]>(ENDPOINTS.OBJECTS),

  getObject: (objectId: string): Promise<SystemObject> =>
    apiClient.get<SystemObject>(ENDPOINTS.OBJECT(objectId)),

  getObjectByName: (objectName: string): Promise<SystemObject> =>
    apiClient.get<SystemObject>(ENDPOINTS.OBJECT_BY_NAME(objectName)),

  // Object Fields
  getAllFields: (): Promise<SystemObjectField[]> =>
    apiClient.get<SystemObjectField[]>(ENDPOINTS.OBJECT_FIELDS),

  getObjectFields: (objectId: string): Promise<SystemObjectField[]> =>
    apiClient.get<SystemObjectField[]>(ENDPOINTS.OBJECT_FIELDS_BY_ID(objectId)),

  // Roles
  // Backend ucu tüm rollerin kayıtlarını döner; roleId ile burada süzülür.
  // (Süzülmezse aynı objectId için son gelen rol kazanır → yanlış rolün yetkisi uygulanır.)
  getRoleObjects: async (roleId: string): Promise<SystemRoleObject[]> => {
    const all = await apiClient.get<SystemRoleObject[]>(ENDPOINTS.ROLE_OBJECTS);
    const wanted = (roleId || '').toLowerCase();
    return (all || []).filter((ro) => (ro.roleId || '').toLowerCase() === wanted);
  },

  getRoleObjectFields: (objectId: string, roleId: string): Promise<SystemRoleObjectField[]> =>
    apiClient.get<SystemRoleObjectField[]>(ENDPOINTS.ROLE_OBJECT_FIELDS(objectId, roleId)),

  // Object Details (sub-objects)
  getObjectDetails: (objectId: string) =>
    apiClient.get(ENDPOINTS.OBJECT_DETAILS_BY_ID(objectId)),

  // Object Filters
  getObjectFilters: (objectId: string): Promise<SystemObjectFilter[]> =>
    apiClient.get<SystemObjectFilter[]>(ENDPOINTS.OBJECT_FILTERS(objectId)),

  // Object Actions (Veri İşlemleri)
  getObjectActions: (objectId: string): Promise<SystemObjectAction[]> =>
    apiClient.get<SystemObjectAction[]>(ENDPOINTS.OBJECT_ACTIONS(objectId)),

  getRoleObjectActions: (objectId: string, roleId: string): Promise<SystemRoleObjectAction[]> =>
    apiClient.get<SystemRoleObjectAction[]>(ENDPOINTS.ROLE_OBJECT_ACTIONS(objectId, roleId)),

  // HTML Card Templates
  getHtmlCardTemplate: (templateId: string): Promise<SystemHtmlCardTemplate> =>
    apiClient.get<SystemHtmlCardTemplate>(ENDPOINTS.HTML_CARD_TEMPLATE(templateId)),

  // HTML Summary Templates
  getHtmlSummaryTemplate: (templateId: string): Promise<SystemHtmlSummaryTemplate> =>
    apiClient.get<SystemHtmlSummaryTemplate>(ENDPOINTS.HTML_SUMMARY_TEMPLATE(templateId)),

  // Razor Templates
  getRazorTemplate: (templateId: string): Promise<SystemRazorTemplate> =>
    apiClient.get<SystemRazorTemplate>(ENDPOINTS.RAZOR_TEMPLATE(templateId)),

  // Metadata damgası — önbelleğin bayat olup olmadığını anlamak için
  getMetadataStamp: (): Promise<MetadataStampResponse> =>
    apiClient.get<MetadataStampResponse>(ENDPOINTS.METADATA_STAMP),

  // Menü ikonları (iconId → PNG base64)
  getIcons: (): Promise<SystemIcon[]> =>
    apiClient.get<SystemIcon[]>(ENDPOINTS.ICONS),

  // Barkod işlem tipleri (nesne bazlı, DisplayOrder sıralı)
  getNodeActionTypes: (objectId: string): Promise<SystemNodeActionType[]> =>
    apiClient.get<SystemNodeActionType[]>(ENDPOINTS.NODE_ACTION_TYPES(objectId)),

  // Özet tipleri
  getSummaryTypes: (): Promise<Array<{ summaryTypeId: string; summaryTypeName: string }>> =>
    apiClient.get<Array<{ summaryTypeId: string; summaryTypeName: string }>>(ENDPOINTS.SUMMARY_TYPES),

  // Rapor tanımları (SP ile rapor tetiklemede ad çözümü için)
  getReportList: (): Promise<Array<{ reportId: string; reportName: string }>> =>
    apiClient.get<Array<{ reportId: string; reportName: string }>>(ENDPOINTS.REPORT_LIST),

  // Worker görevi (Print / Mail) — backend SystemWorkerTask alanlarını camelCase kabul eder
  createWorkerTask: async (task: WorkerTaskCreateRequest): Promise<{ success: boolean; message: string | null }> => {
    const res = await apiClient.post<{ success?: boolean; Success?: boolean; message?: string; Message?: string }>(ENDPOINTS.WORKER_TASKS, task);
    return { success: !!(res?.success ?? res?.Success), message: (res?.message ?? res?.Message) ?? null };
  },
};

export interface WorkerTaskCreateRequest {
  workerTaskTypeId: number;
  workerTaskStateId: number;
  taskName: string;
  executeAfter: string;
  isRepetitive: boolean;
  addUser?: number | null;
  attachments: Array<{
    name: string;
    attachmentFileName: string | null;
    printerName: string | null;
    copies: number;
    addUser?: number | null;
    parameters: Array<{ fieldName: string; filterOperatorType: string; value: string | null; addUser?: number | null }>;
  }>;
}
